import {
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import {useNavigate, useParams, useSearchParams} from "react-router-dom";
import {useSelector} from "react-redux";
import moment from "moment";

import Loading from "@/components/Loading";
import VLFSelectNew from "@/components/Input/SelectNew";
import VLFTextField from "@/components/Input/TextField";
import VLFModal from "@/components/Modal";
import ChoiceChips from "@/components/ChoiceChips";
import MainActions from "@/components/MainActions";

import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import GroupIcon from "@mui/icons-material/Group";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import SendIcon from "@mui/icons-material/Send";

import {MessagesAPI} from "@/api/messages";
import {
	conversationDisplayName,
	messageUserId,
	messageUserName as fullName,
} from "@/utils/messages";
import {UserAPI} from "@/api/user";

import "./index.scss";

// ── Helpers ───────────────────────────────────────────────────────────────────

const POLL_THREAD_MS = 5000;
const POLL_CONV_MS = 10000;

const initials = (name = "") =>
	name
		.split(" ")
		.slice(0, 2)
		.map((w) => w[0] ?? "")
		.join("")
		.toUpperCase();

const fmtTime = (ts) => {
	if (!ts) return "";
	const m = moment(ts);
	if (m.isSame(moment(), "day")) return m.format("HH:mm");
	if (m.isSame(moment().subtract(1, "day"), "day")) return "ieri";
	if (m.isAfter(moment().subtract(6, "days"))) return m.format("ddd");
	return m.format("DD/MM/YY");
};

// ── Avatar ────────────────────────────────────────────────────────────────────

function Avatar({name, size = 36}) {
	return (
		<div
			className="msg-avatar"
			style={{width: size, height: size, fontSize: size * 0.36}}
		>
			{initials(name)}
		</div>
	);
}

// ── Conversation row ──────────────────────────────────────────────────────────

function ConvRow({conv, active, onClick}) {
	const unread = conv.unreadCount ?? 0;
	const preview = conv.lastMessage?.content ?? "";
	const name = conv.name ?? conv.lastMessage?.senderName ?? "—";

	return (
		<button
			className={"msg-conv-row" + (active ? " msg-conv-row--active" : "")}
			onClick={onClick}
		>
			<div className="msg-conv-row__avatar">
				<Avatar name={name} size={38} />
				{conv.isGroup && (
					<span className="msg-conv-row__group-dot">
						<GroupIcon sx={{fontSize: 10}} />
					</span>
				)}
			</div>
			<div className="msg-conv-row__body">
				<div className="msg-conv-row__top">
					<span className="msg-conv-row__name">{name}</span>
					<span className="msg-conv-row__time">{fmtTime(conv.updatedAt)}</span>
				</div>
				<div className="msg-conv-row__bottom">
					<span className="msg-conv-row__preview">{preview}</span>
					{unread > 0 && (
						<span className="msg-conv-row__badge">
							{unread > 99 ? "99+" : unread}
						</span>
					)}
				</div>
			</div>
		</button>
	);
}

// ── Message bubble ────────────────────────────────────────────────────────────

function Bubble({msg, isMine, showSender, senderDisplayName, onDelete}) {
	const [hover, setHover] = useState(false);

	if (msg.isDeleted) {
		return (
			<div
				className={"msg-bubble-wrap" + (isMine ? " msg-bubble-wrap--mine" : "")}
			>
				<span className="msg-bubble msg-bubble--deleted">
					Messaggio eliminato
				</span>
			</div>
		);
	}

	return (
		<div
			className={"msg-bubble-wrap" + (isMine ? " msg-bubble-wrap--mine" : "")}
			onMouseEnter={() => setHover(true)}
			onMouseLeave={() => setHover(false)}
		>
			{!isMine && showSender && (
				<span className="msg-bubble__sender">{senderDisplayName}</span>
			)}
			<div
				className={"msg-bubble-row" + (isMine ? " msg-bubble-row--mine" : "")}
			>
				{!isMine && <Avatar name={senderDisplayName || "?"} size={28} />}
				<div className="msg-bubble__actions">
					{isMine && hover && (
						<button
							className="msg-bubble__delete"
							onClick={() => onDelete(msg.id)}
							title="Elimina"
						>
							<DeleteOutlineIcon sx={{fontSize: 14}} />
						</button>
					)}
					<div
						className={
							"msg-bubble" +
							(isMine ? " msg-bubble--mine" : " msg-bubble--theirs")
						}
					>
						{msg.content}
						<span className="msg-bubble__time">
							{moment(msg.sentAt).format("HH:mm")}
						</span>
					</div>
				</div>
			</div>
		</div>
	);
}

// ── New conversation modal ────────────────────────────────────────────────────

function NewConvModal({open, close, users, onCreated}) {
	const [type, setType] = useState("direct"); // "direct" | "group"
	const [selectedUser, setSelectedUser] = useState(null);
	const [groupName, setGroupName] = useState("");
	const [groupMembers, setGroupMembers] = useState([]);
	const [loading, setLoading] = useState(false);

	const reset = () => {
		setType("direct");
		setSelectedUser(null);
		setGroupName("");
		setGroupMembers([]);
	};

	const handleClose = () => {
		reset();
		close();
	};

	const handleConfirm = async () => {
		setLoading(true);
		try {
			let conv;
			if (type === "direct") {
				conv = await MessagesAPI.getOrCreateDirect({
					otherUserId: selectedUser?.value,
				});
			} else {
				conv = await MessagesAPI.createGroup({
					name: groupName,
					participantIds: groupMembers.map((m) => m.value),
				});
			}
			onCreated(conv);
			handleClose();
		} catch {
			// noop
		} finally {
			setLoading(false);
		}
	};

	const canConfirm =
		type === "direct"
			? !!selectedUser
			: groupName.trim() && groupMembers.length > 0;

	return (
		<VLFModal
			open={open}
			title="Nuova conversazione"
			close={handleClose}
			confirm={handleConfirm}
			confirmDisabled={!canConfirm || loading}
			maxWidth="sm"
			content={
				<div className="msg-newconv">
					{/* Type toggle */}
					<ChoiceChips
						options={[
							{value: "direct", label: "Diretta"},
							{value: "group", label: "Gruppo"},
						]}
						value={type}
						onChange={(value) => {
							setType(value);
							setSelectedUser(null);
							setGroupMembers([]);
						}}
					/>

					{type === "direct" ? (
						<VLFSelectNew
							options={users}
							value={selectedUser}
							onChange={setSelectedUser}
							label="Utente"
						/>
					) : (
						<>
							<VLFTextField
								label="Nome gruppo"
								value={groupName}
								onChange={(e) => setGroupName(e.target.value)}
								size="small"
							/>
							<VLFSelectNew
								options={users}
								value={groupMembers}
								onChange={setGroupMembers}
								label="Partecipanti"
								isMulti
							/>
						</>
					)}
				</div>
			}
		/>
	);
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function MessagesPage({handleNotification}) {
	const navigate = useNavigate();
	const {conversationId} = useParams();
	const [searchParams, setSearchParams] = useSearchParams();
	const currentUserId = useSelector((s) => s?.user?.userId);

	// ── State ─────────────────────────────────────────────────────────────────

	const [loading, setLoading] = useState(false);
	const [conversations, setConversations] = useState([]);
	const [messages, setMessages] = useState([]);
	const [text, setText] = useState("");
	const [users, setUsers] = useState([]);
	const [openNewConv, setOpenNewConv] = useState(false);
	const [sending, setSending] = useState(false);
	const [openAddUser, setOpenAddUser] = useState(false);
	const [addUserSelected, setAddUserSelected] = useState(null);
	const [addUserLoading, setAddUserLoading] = useState(false);

	const threadRef = useRef(null);
	const inputRef = useRef(null);
	const convPollRef = useRef(null);
	const threadPollRef = useRef(null);

	const activeConv = useMemo(
		() =>
			conversations.find((c) => String(c.id) === String(conversationId)) ??
			null,
		[conversations, conversationId],
	);

	// ── Data fetching ─────────────────────────────────────────────────────────

	const loadConversations = useCallback(async () => {
		try {
			const data = await MessagesAPI.getConversations();
			setConversations(data ?? []);
		} catch {
			// silently ignore poll errors
		}
	}, []);

	const loadMessages = useCallback(async (convId, silent = false) => {
		if (!convId) return;
		if (!silent) setLoading(true);
		try {
			const data = await MessagesAPI.getMessages(convId);
			setMessages(Array.isArray(data) ? data : data?.items ?? []);
		} catch {
			// noop
		} finally {
			if (!silent) setLoading(false);
		}
	}, []);

	const loadUsers = useCallback(async () => {
		try {
			const data = await UserAPI.list();
			setUsers(
				(data ?? [])
					.filter((u) => String(u.userId ?? u.id) !== String(currentUserId))
					.map((u) => ({
						value: u.userId ?? u.id,
						label:
							[u.firstName, u.lastName].filter(Boolean).join(" ") || u.userName,
					})),
			);
		} catch {
			// noop
		}
	}, [currentUserId]);

	// ── Initial load ──────────────────────────────────────────────────────────

	useEffect(() => {
		loadConversations();
		loadUsers();
	}, [loadConversations, loadUsers]);

	// ── Auto-open new-conversation modal when ?new=1 ──────────────────────────

	useEffect(() => {
		if (searchParams.get("new") === "1") {
			setOpenNewConv(true);
			setSearchParams({}, {replace: true});
		}
	}, [searchParams]);

	// ── Poll conversations ────────────────────────────────────────────────────

	useEffect(() => {
		convPollRef.current = setInterval(loadConversations, POLL_CONV_MS);
		return () => clearInterval(convPollRef.current);
	}, [loadConversations]);

	// ── Load + poll messages when conversation changes ────────────────────────

	useEffect(() => {
		clearInterval(threadPollRef.current);
		setMessages([]);

		if (!conversationId) return;

		loadMessages(conversationId);
		MessagesAPI.markRead(conversationId).catch(() => {});

		threadPollRef.current = setInterval(
			() => loadMessages(conversationId, true),
			POLL_THREAD_MS,
		);

		return () => clearInterval(threadPollRef.current);
	}, [conversationId]);

	// ── Auto-scroll to bottom when messages change ────────────────────────────

	useLayoutEffect(() => {
		if (threadRef.current) {
			threadRef.current.scrollTop = threadRef.current.scrollHeight;
		}
	}, [messages]);

	// ── Focus input when conversation opens ───────────────────────────────────

	useEffect(() => {
		if (conversationId) inputRef.current?.focus();
	}, [conversationId]);

	// ── Send message ──────────────────────────────────────────────────────────

	const handleSend = async () => {
		const content = text.trim();
		if (!content || !conversationId || sending) return;
		setSending(true);
		setText("");
		try {
			await MessagesAPI.sendMessage(conversationId, {content});
			await loadMessages(conversationId, true);
			loadConversations();
		} catch {
			setText(content); // restore on error
		} finally {
			setSending(false);
		}
	};

	const handleKeyDown = (e) => {
		if (e.key === "Enter" && !e.shiftKey) {
			e.preventDefault();
			handleSend();
		}
	};

	// ── Delete message ────────────────────────────────────────────────────────

	const handleDeleteMessage = async (messageId) => {
		try {
			await MessagesAPI.deleteMessage(messageId);
			setMessages((prev) =>
				prev.map((m) => (m.id === messageId ? {...m, isDeleted: true} : m)),
			);
		} catch {
			// noop
		}
	};

	// ── Add participant to group ──────────────────────────────────────────────

	const handleAddParticipant = async () => {
		if (!addUserSelected || !conversationId) return;
		setAddUserLoading(true);
		try {
			await MessagesAPI.addParticipant(conversationId, {
				userId: addUserSelected.value,
			});
			await loadConversations();
			setOpenAddUser(false);
			setAddUserSelected(null);
		} catch {
			// noop
		} finally {
			setAddUserLoading(false);
		}
	};

	// ── New conversation created ──────────────────────────────────────────────

	const handleConvCreated = async (conv) => {
		await loadConversations();
		navigate("/messages/" + conv.id);
	};

	// ── Conversation name ─────────────────────────────────────────────────────

	const convName = (conv) => conversationDisplayName(conv, currentUserId);

	const activeConvName = convName(activeConv);

	// ── Group messages by date (oldest first, prevMsg pre-computed) ───────────

	const grouped = useMemo(() => {
		const days = [];
		let lastDate = null;
		let lastMsgItem = null;
		const sorted = [...messages].sort(
			(a, b) => new Date(a.sentAt) - new Date(b.sentAt),
		);
		for (const msg of sorted) {
			const d = moment(msg.sentAt).format("YYYY-MM-DD");
			if (d !== lastDate) {
				days.push({
					type: "date",
					key: d,
					label: moment(msg.sentAt).format("D MMMM YYYY"),
				});
				lastDate = d;
			}
			const item = {
				type: "msg",
				key: msg.id,
				msg,
				prevMsg: lastMsgItem?.msg ?? null,
			};
			days.push(item);
			lastMsgItem = item;
		}
		return days;
	}, [messages]);

	// ── Render ────────────────────────────────────────────────────────────────

	return (
		<div className="msg-page">
			<Loading visible={loading} />

			{/* ── Sidebar ──────────────────────────────────────────────────── */}
			<div
				className={
					"msg-sidebar" + (conversationId ? " msg-sidebar--hidden-mobile" : "")
				}
			>
				<div className="msg-sidebar__header">
					<h1 className="msg-sidebar__title">Messaggi</h1>
					<MainActions
						actions={[
							{
								icon: <AddIcon />,
								title: "Nuova conversazione",
								onClick: () => setOpenNewConv(true),
							},
						]}
					/>
				</div>

				<div className="msg-sidebar__list">
					{conversations.length === 0 ? (
						<div className="msg-sidebar__empty">Nessuna conversazione</div>
					) : (
						conversations.map((conv) => (
							<ConvRow
								key={conv.id}
								conv={{...conv, name: convName(conv)}}
								active={String(conv.id) === String(conversationId)}
								onClick={() => navigate("/messages/" + conv.id)}
							/>
						))
					)}
				</div>
			</div>

			{/* ── Thread ───────────────────────────────────────────────────── */}
			<div
				className={
					"msg-thread" + (!conversationId ? " msg-thread--hidden-mobile" : "")
				}
			>
				{!conversationId ? (
					<div className="msg-thread__empty">
						<span>Seleziona una conversazione</span>
					</div>
				) : (
					<>
						{/* Header */}
						<div className="msg-thread__header">
							<div className="msg-thread__back">
								<MainActions
									actions={[
										{
											icon: <ArrowBackIcon />,
											title: "Indietro",
											color: "info",
											onClick: () => navigate("/messages"),
										},
									]}
								/>
							</div>
							<Avatar name={activeConvName} size={32} />
							<div className="msg-thread__info">
								<span className="msg-thread__name">{activeConvName}</span>
								{activeConv?.isGroup && (
									<span className="msg-thread__meta">
										{activeConv?.participants?.length ?? 0} partecipanti
									</span>
								)}
							</div>
							{activeConv?.isGroup && (
								<MainActions
									actions={[
										{
											icon: <PersonAddIcon />,
											title: "Aggiungi partecipante",
											variant: "outlined",
											onClick: () => setOpenAddUser(true),
										},
									]}
								/>
							)}
						</div>

						{/* Messages */}
						<div className="msg-thread__body" ref={threadRef}>
							{grouped.map((item) => {
								if (item.type === "date") {
									return (
										<div key={item.key} className="msg-date-sep">
											{item.label}
										</div>
									);
								}
								const msg = item.msg;
								const isMine = String(msg.sender?.id) === String(currentUserId);
								const {prevMsg} = item;
								const showSender =
									!isMine &&
									activeConv?.isGroup &&
									(!prevMsg || prevMsg.sender?.id !== msg.sender?.id);
								const senderDisplayName = fullName(msg.sender) || "?";

								return (
									<Bubble
										key={msg.id}
										msg={msg}
										isMine={isMine}
										showSender={showSender}
										senderDisplayName={senderDisplayName}
										onDelete={handleDeleteMessage}
									/>
								);
							})}
						</div>

						{/* Input */}
						<div className="msg-thread__input-bar">
							<textarea
								ref={inputRef}
								className="msg-thread__input"
								placeholder="Scrivi un messaggio…"
								value={text}
								onChange={(e) => setText(e.target.value)}
								onKeyDown={handleKeyDown}
								rows={1}
							/>
							<button
								className="msg-thread__send"
								onClick={handleSend}
								disabled={!text.trim() || sending}
								title="Invia (Invio)"
							>
								<SendIcon sx={{fontSize: 18}} />
							</button>
						</div>
					</>
				)}
			</div>

			{/* ── New conversation modal ────────────────────────────────────── */}
			<NewConvModal
				open={openNewConv}
				close={() => setOpenNewConv(false)}
				users={users}
				onCreated={handleConvCreated}
			/>

			{/* ── Add participant modal ─────────────────────────────────────── */}
			<VLFModal
				open={openAddUser}
				title="Aggiungi partecipante"
				close={() => {
					setOpenAddUser(false);
					setAddUserSelected(null);
				}}
				confirm={handleAddParticipant}
				confirmDisabled={!addUserSelected || addUserLoading}
				maxWidth="xs"
				content={
					<div className="msg-add-user">
						<VLFSelectNew
							options={users.filter(
								(u) =>
									!(activeConv?.participants ?? []).some(
										(p) => String(messageUserId(p)) === String(u.value),
									),
							)}
							value={addUserSelected}
							onChange={setAddUserSelected}
							label="Utente"
						/>
					</div>
				}
			/>
		</div>
	);
}
